import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { ScrollShadow } from './utils/ScrollShadow'
import {
  FEEDBACK_COLORS, FEEDBACK_LABELS, getOptionFeedbackStatus,
  OPTION_FEEDBACK_STYLE, FEEDBACK_BADGE,
} from './utils/feedbackStyles'

const OPTION_BASE = {
  display: 'flex', alignItems: 'center',
  border: `2px solid ${FEEDBACK_COLORS.neutral.border}`,
  borderRadius: '0.25rem', marginBottom: '0.25rem',
  padding: '0.25rem 0.5rem', backgroundColor: FEEDBACK_COLORS.neutral.bg,
  gap: '0.4rem',
}

/**
 * Parse saved answer JSON into a selections map: { "optGuid": boolean }
 */
function parseAnswer(answer) {
  if (!answer) return {}
  try {
    return JSON.parse(answer)
  } catch {
    return {}
  }
}

export const NGNGroupingQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  const [selections, setSelections] = useState(() => parseAnswer(question.answer))

  useEffect(() => {
    const selected = Object.keys(JSON.parse(question.answer || '{}')).length
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const save = useCallback(async (newSelections) => {
    const answerJson = JSON.stringify(newSelections)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })
    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [coursenum, activitySubmissionId, question.guid, dispatch])

  const handleChange = useCallback((optGuid) => {
    if (readonly || isSaving) return

    setSelections(prev => {
      const next = { ...prev }
      next[optGuid] = !next[optGuid]
      save(next)
      return next
    })
  }, [readonly, isSaving, save])

  // The first column (index 0) is the row-text header; data columns start at index 1
  const columns = question.data.tableColumns || []
  const rows = question.data.tableRows || []
  const cellOptions = question.data.cellOptions || {}

  // Build lookup: for each row, for each data column, get the options
  const getCellOpts = useCallback((rowIdx, colIdx) => {
    const key = `${rowIdx}-${colIdx}`
    return cellOptions[key] || []
  }, [cellOptions])

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const correctGuids = useMemo(() => {
    if (!showFeedback || !showAnswer || !question.correctAnswer) return null
    return new Set(question.correctAnswer)
  }, [showFeedback, showAnswer, question.correctAnswer])

  // Data columns are all columns except the first (which is the row-text header)
  const dataColumns = columns.slice(1)

  return (
    <>
      <FeedbackDisplay question={question} />
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div
        role="heading"
        aria-level={3}
        className="mb-2 text-left"
        dangerouslySetInnerHTML={{ __html: question.text }}
      />

      <ScrollShadow>
        <table className="table table-bordered mb-0">
          <thead className="bg-light">
            <tr>
              {columns.map(col => (
                <th key={col.guid || col.idx} scope="col" className="text-center font-weight-bold" dangerouslySetInnerHTML={{ __html: col.title || '' }} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.guid}>
                <td className="align-middle" dangerouslySetInnerHTML={{ __html: row.text || '' }} />
                {dataColumns.map((col, dataColIdx) => {
                  const opts = getCellOpts(row.idx, dataColIdx)
                  return (
                    <td key={col.guid || col.idx}>
                      {opts.map(opt => {
                        const checked = !!selections[opt.guid]
                        const isCorrectOpt = correctGuids && correctGuids.has(opt.guid)

                        let optionStyle = OPTION_BASE
                        let badge = null
                        if (correctGuids) {
                          const status = getOptionFeedbackStatus(checked, isCorrectOpt)
                          optionStyle = { ...OPTION_BASE, ...OPTION_FEEDBACK_STYLE[status] }
                          if (status !== 'neutral') {
                            badge = <span style={FEEDBACK_BADGE[status]}>{FEEDBACK_LABELS[status]}</span>
                          }
                        }

                        return (
                          <div key={opt.guid} style={optionStyle}>
                            <input
                              type="checkbox"
                              id={`grouping-${opt.guid}`}
                              checked={checked}
                              disabled={readonly || isSaving}
                              onChange={() => handleChange(opt.guid)}
                              className="flex-shrink-0"
                            />
                            <label
                              htmlFor={`grouping-${opt.guid}`}
                              className={`mb-0 flex-grow-1 ${readonly || isSaving ? '' : 'cursor-pointer'}`}
                              style={{ userSelect: 'none' }}
                              dangerouslySetInnerHTML={{ __html: opt.text || '' }}
                            />
                            {badge}
                          </div>
                        )
                      })}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollShadow>
      </TabSplitLayout>
    </>
  )
}
